import React, { useEffect } from 'react';
import { UserButton } from '@clerk/clerk-react';
import { NavLink } from 'react-router-dom';
import CgToaster from 'modules/Common/ui/toast/CgToaster';
import { useBranding } from 'modules/Common/hooks/useBranding';

/**
 * The persistent frame every admin route renders inside.
 *
 * CgDataManagementPage is deliberately not reused (D11). Not because of weight —
 * measured, it costs 17 files and zero non-allowlisted feature modules — but
 * because of fit: it renders CgBreadcrumb, which autodetects a trail from
 * location.pathname to place a page inside the CUSTOMER information architecture.
 * This console is one route deep with no hierarchy, so that trail is either empty
 * or lying.
 *
 * The header is mainly a FRAME. It answers the three questions an admin needs
 * answered before they change anything: which brand this deployment is, which
 * environment they are pointed at, and who they are signed in as. The console is
 * still flat (one level, no hierarchy), so navigation is just a row of links.
 */

const NAV_LINKS = [
  { to: '/', label: 'Organizations', exact: true },
  { to: '/erp-demand', label: 'ERP demand' },
];

// PROD is the only value that must never be mistaken for anything else, so it is
// the one that gets a colour. Everything else is informational.
const ENV_TONES = {
  PROD: 'bg-red-50 text-red-700 ring-red-200',
};

const envName = () => process.env.REACT_APP_ENV_MODE || 'LOCAL';

export default function AdminShell({ children }) {
  const brand = useBranding();
  const env = envName();

  // Re-assert the tab title, because something else has already taken it.
  // useBranding's applyBranding() runs at MODULE LOAD and sets
  // `document.title = brand.name` (useBranding.js:137) — written for the
  // Faradai-on-the-shared-CG-build case, where the baked HTML title is wrong.
  // It overwrites admin.html's "<brand> · Admin" with the bare brand name, so
  // the console's tab becomes indistinguishable from the customer app's. With
  // both open — which is the normal state while administering — you cannot tell
  // which tab force-expires a plan. Caught by the E2E suite; jsdom never runs
  // that module-load side effect against a real document.
  useEffect(() => {
    document.title = `${brand.name} · Admin`;
  }, [brand.name]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-3 px-4">
          {brand.logoUrl ? (
            <img
              src={brand.logoUrl}
              alt=""
              className="h-6 w-auto"
            />
          ) : null}
          <span className="text-sm font-semibold text-slate-900">{brand.name}</span>
          <span className="text-sm text-slate-400">Admin</span>

          {/*
            The environment name is the cheap guard against the failure this console
            makes possible: force-expiring the wrong organisation's plan because the
            tab looked like QA and was production. Both are served from the same
            image at similar hostnames, so the page has to say which it is.
          */}
          <span
            data-testid="admin-env-badge"
            className={`ml-2 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
              ENV_TONES[env] ?? 'bg-slate-100 text-slate-600 ring-slate-200'
            }`}
          >
            {env}
          </span>

          <nav className="ml-6 flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                exact={link.exact}
                className="rounded-lg px-2.5 py-1 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                activeClassName="bg-slate-100 font-medium text-slate-900"
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center">
            <UserButton afterSignOutUrl="/" />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8">{children}</main>

      {/*
        Mounted here and nowhere else, because this is the only component guaranteed
        mounted for every route. cgToast() pushes into a module-level store that
        renders nothing on its own; CgToaster is the single host subscribed to it,
        and the customer app mounts it in _routes.jsx — which this app never renders.
        Without this line every mutation toast goes to a store nobody is listening
        to: no error, no toast, the dialog just closes.
      */}
      <CgToaster />
    </div>
  );
}
